/**
 * useTenantBranding — detects the current tenant from either:
 *   1. The subdomain (acme.mykeyboss.com → "acme")
 *   2. A ?brand=acme query param (handy for previewing on the dev URL)
 *
 * Returns { branding, loading }. `branding` is { name, logo_url, slug } or null.
 * Silently returns null when no tenant is detected — pages then render the
 * default/un-branded experience.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const APEX_HOSTS = new Set([
  'mykeyboss.com',
  'www.mykeyboss.com',
  'localhost',
]);

// Hosts that we KNOW are dev/preview environments where the "subdomain" is just
// the project slug, not a tenant slug. We never look those up.
const DEV_HOST_SUFFIXES = [
  '.preview.emergentagent.com',
  '.preview.emergentcf.cloud',
  '.cluster-3.preview.emergentcf.cloud',
];

function extractSlug() {
  // 1. Query-param override always wins (for previewing branded experience on dev URL)
  try {
    const u = new URL(window.location.href);
    const q = u.searchParams.get('brand');
    if (q) return q.toLowerCase();
  } catch {
    /* noop */
  }

  const host = window.location.hostname.toLowerCase();
  if (APEX_HOSTS.has(host)) return null;
  if (DEV_HOST_SUFFIXES.some((s) => host.endsWith(s))) return null;

  // mykeyboss.com derivatives: "acme.mykeyboss.com" → "acme"
  if (host.endsWith('.mykeyboss.com')) {
    return host.slice(0, -('.mykeyboss.com'.length));
  }

  // Generic fallback: first label of a 3+-label hostname
  const parts = host.split('.');
  if (parts.length >= 3) return parts[0];
  return null;
}

export default function useTenantBranding() {
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const slug = extractSlug();
    if (!slug) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/branding/by-subdomain/${slug}`);
        if (!cancelled) setBranding(data);
      } catch {
        if (!cancelled) setBranding(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return { branding, loading };
}
